/**
 * LineHeight Extension for Tiptap
 * 
 * Adds line-height attribute to block-level elements (paragraph, heading, etc.)
 * Provides setLineHeight command to apply line height ONLY to selected blocks
 * 
 * Usage:
 *   editor.chain().focus().setLineHeight('1.5').run()
 *   editor.chain().focus().unsetLineHeight().run()
 */

import { Extension } from '@tiptap/core';

export interface LineHeightOptions {
    /**
     * The types of nodes where the line-height can be applied
     * @default ['paragraph', 'heading', 'listItem', 'taskItem']
     */
    types: string[];
    /**
     * The default line-height
     * @default '1.5'
     */
    defaultLineHeight: string;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        lineHeight: {
            /**
             * Set the line height attribute for selected blocks
             */
            setLineHeight: (lineHeight: string) => ReturnType;
            /**
             * Unset the line height attribute for selected blocks
             */
            unsetLineHeight: () => ReturnType;
        };
    }
}

export const LineHeight = Extension.create<LineHeightOptions>({
    name: 'lineHeight',

    addOptions() {
        return {
            types: ['paragraph', 'heading', 'listItem', 'taskItem'],
            defaultLineHeight: '1.5',
        };
    },

    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    lineHeight: {
                        default: null,
                        parseHTML: (element) => element.style.lineHeight || null,
                        renderHTML: (attributes) => {
                            if (!attributes.lineHeight) {
                                return {};
                            }
                            return {
                                style: `line-height: ${attributes.lineHeight}`,
                            };
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setLineHeight:
                (lineHeight: string) =>
                    ({ tr, state, dispatch }) => {
                        const { selection } = state;
                        const { from, to } = selection;

                        // Find all block nodes in the selection and update their lineHeight
                        state.doc.nodesBetween(from, to, (node, pos) => {
                            if (this.options.types.includes(node.type.name)) {
                                if (dispatch) {
                                    tr.setNodeMarkup(pos, undefined, {
                                        ...node.attrs,
                                        lineHeight,
                                    });
                                }
                            }
                        });

                        return true;
                    },
            unsetLineHeight:
                () =>
                    ({ tr, state, dispatch }) => {
                        const { selection } = state;
                        const { from, to } = selection;

                        // Find all block nodes in the selection and remove their lineHeight
                        state.doc.nodesBetween(from, to, (node, pos) => {
                            if (this.options.types.includes(node.type.name)) {
                                if (dispatch) {
                                    const { lineHeight, ...rest } = node.attrs;
                                    tr.setNodeMarkup(pos, undefined, rest);
                                }
                            }
                        });

                        return true;
                    },
        };
    },
});

export default LineHeight;
